import { Button, Dropdown, Layer, Tooltip } from '@carbon/react';
import { LockableInput } from 'components/well-page-content/decline-curve-analysis/lockable-input';
import type { MineralType } from 'types/minerals';
import { useTcDcaForm } from './tc-dca-form-context';
import type {
  DeclineModel,
  RampUpModel,
  StreamDcaParams,
  TcDcaFormValues,
} from './types';
import styles from './tc-dca-configuration-panel.module.scss';

const DECLINE_MODEL_ITEMS: Array<{ id: DeclineModel; label: string }> = [
  { id: 'modified_hyperbolic', label: 'Modified Hyperbolic' },
  { id: 'hyperbolic', label: 'Hyperbolic' },
  { id: 'exponential', label: 'Exponential' },
  { id: 'ratio', label: 'Ratio' },
];

const RAMP_UP_MODEL_ITEMS: Array<{ id: RampUpModel; label: string }> = [
  { id: 'none', label: 'None' },
  { id: 'constant', label: 'Constant' },
  { id: 'linear', label: 'Linear' },
  { id: 'exponential', label: 'Exponential' },
];

interface TcDcaVolumeModelProps {
  stream: MineralType;
}

const STREAM_FIELD_MAP: Record<MineralType, keyof TcDcaFormValues> = {
  oil: 'oilDcaParams',
  gas: 'gasDcaParams',
  water: 'waterDcaParams',
};

/**
 * Returns true when a stream's DCA model is a valid primary (non-ratio, non-exponential).
 * @param {StreamDcaParams} params - The stream's DCA parameters to check.
 * @return {boolean} Whether the stream qualifies as a primary model.
 */
function isPrimaryModel(params: StreamDcaParams): boolean {
  return (
    params.dcaModel !== 'ratio' &&
    params.dcaModel !== 'exponential' &&
    params.qi !== null
  );
}

/**
 * Extracts the first validation error string from a TanStack Form field's meta.
 * @param {any} field - The TanStack form field instance.
 * @return {string | undefined} The first error message, or undefined if none.
 */
function getFirstFieldError(field: any): string | undefined {
  const errors = field.state?.meta?.errors;
  if (!errors || !Array.isArray(errors) || errors.length === 0)
    return undefined;
  const first = errors[0];
  return typeof first === 'string' ? first : first ? String(first) : undefined;
}

export const TcDcaVolumeModel = ({ stream }: TcDcaVolumeModelProps) => {
  const { form, lockStates, setLockParam, unlockParam } = useTcDcaForm();
  const fieldKey = STREAM_FIELD_MAP[stream];
  const locks = lockStates[stream];

  const isLocked = (type: 'dca' | 'rampUp', param: string) =>
    param in locks[type];

  const toggleLock = (
    type: 'dca' | 'rampUp',
    param: string,
    value: number | null
  ) => {
    if (isLocked(type, param)) {
      unlockParam(stream, type, param);
    } else {
      setLockParam(stream, type, param, value);
    }
  };

  const productionUnit = stream === 'gas' ? 'mcf' : 'bbl';

  return (
    <div className={styles.volumeModelContainer}>
      {/* Ramp-up Model */}
      <form.Subscribe
        selector={(s: any) => s.values[fieldKey].dcaModel as DeclineModel}
      >
        {(dcaModel: DeclineModel) => (
          <form.Field name={`${fieldKey}.rampUpModel` as never}>
            {(field: any) => (
              <Layer>
                <Dropdown
                  id={`tc-dca-${stream}-rampup-model`}
                  titleText="Ramp-up Model"
                  label="None"
                  items={RAMP_UP_MODEL_ITEMS}
                  itemToString={(item) => item?.label ?? ''}
                  selectedItem={
                    RAMP_UP_MODEL_ITEMS.find(
                      (i) => i.id === field.state.value
                    ) ?? RAMP_UP_MODEL_ITEMS[0]
                  }
                  onChange={({ selectedItem }) => {
                    if (selectedItem) field.handleChange(selectedItem.id);
                  }}
                  disabled={dcaModel === 'ratio'}
                  className={
                    dcaModel === 'ratio' ? styles.disabledField : undefined
                  }
                />
              </Layer>
            )}
          </form.Field>
        )}
      </form.Subscribe>

      {/* Ramp-up params (visible when rampUpModel !== 'none') */}
      <form.Subscribe
        selector={(s: any) => ({
          rampUpModel: s.values[fieldKey].rampUpModel as RampUpModel,
          dcaModel: s.values[fieldKey].dcaModel as DeclineModel,
        })}
      >
        {({
          rampUpModel,
          dcaModel,
        }: {
          rampUpModel: RampUpModel;
          dcaModel: DeclineModel;
        }) => {
          if (rampUpModel === 'none' || dcaModel === 'ratio') return null;

          return (
            <>
              <div className={styles.volumeModelRow}>
                <form.Field name={`${fieldKey}.rampUpQi` as never}>
                  {(field: any) => {
                    const error = getFirstFieldError(field);
                    return (
                      <Layer>
                        <LockableInput
                          inputType="number"
                          inputMode="numeric"
                          isLocked={isLocked('rampUp', 'qi')}
                          onToggleLock={() =>
                            toggleLock('rampUp', 'qi', field.state.value)
                          }
                          id={`tc-dca-${stream}-rampup-qi`}
                          labelText={`qi ${productionUnit}/month`}
                          max={100_000_000}
                          min={rampUpModel === 'linear' ? -100_000_000 : 0}
                          size="md"
                          value={field.state.value ?? ''}
                          placeholder="0"
                          invalid={!!error}
                          invalidText={error || ''}
                          onChange={(val) => field.handleChange(Number(val))}
                        />
                      </Layer>
                    );
                  }}
                </form.Field>

                <form.Field name={`${fieldKey}.rampUpDuration` as never}>
                  {(field: any) => {
                    const error = getFirstFieldError(field);
                    return (
                      <Layer>
                        <LockableInput
                          inputType="number"
                          inputMode="numeric"
                          isLocked={isLocked('rampUp', 'tRamp')}
                          onToggleLock={() =>
                            toggleLock('rampUp', 'tRamp', field.state.value)
                          }
                          id={`tc-dca-${stream}-rampup-duration`}
                          labelText="Ramp-up Duration months"
                          max={100_000}
                          min={0}
                          size="md"
                          value={field.state.value ?? ''}
                          placeholder="0"
                          invalid={!!error}
                          invalidText={error || ''}
                          onChange={(val) => field.handleChange(Number(val))}
                        />
                      </Layer>
                    );
                  }}
                </form.Field>
              </div>

              {(rampUpModel === 'linear' || rampUpModel === 'exponential') && (
                <form.Field name={`${fieldKey}.rampUpN` as never}>
                  {(field: any) => {
                    const error = getFirstFieldError(field);
                    return (
                      <Layer>
                        <LockableInput
                          inputType="number"
                          inputMode="decimal"
                          isLocked={isLocked('rampUp', 'n')}
                          onToggleLock={() =>
                            toggleLock('rampUp', 'n', field.state.value)
                          }
                          id={`tc-dca-${stream}-rampup-n`}
                          labelText={
                            rampUpModel === 'linear'
                              ? `n ${productionUnit}/month^2`
                              : 'n 1/month'
                          }
                          max={rampUpModel === 'linear' ? 1_000_000 : 9.9999}
                          min={rampUpModel === 'linear' ? -1_000_000 : 0.0001}
                          size="md"
                          value={field.state.value ?? ''}
                          placeholder={
                            rampUpModel === 'linear' ? '0.00' : '0.0000'
                          }
                          invalid={!!error}
                          invalidText={error || ''}
                          onChange={(val) => field.handleChange(Number(val))}
                        />
                      </Layer>
                    );
                  }}
                </form.Field>
              )}
            </>
          );
        }}
      </form.Subscribe>

      {/* DCA Model */}
      <form.Subscribe
        selector={(s: any) => ({
          oilParams: s.values.oilDcaParams as StreamDcaParams,
          gasParams: s.values.gasDcaParams as StreamDcaParams,
        })}
      >
        {({
          oilParams,
          gasParams,
        }: {
          oilParams: StreamDcaParams;
          gasParams: StreamDcaParams;
        }) => {
          // Ratio is only available when another stream has a valid primary model
          const isOilPrimary = isPrimaryModel(oilParams);
          const isGasPrimary = isPrimaryModel(gasParams);
          let isRatioAvailable: boolean;
          if (stream === 'oil') {
            isRatioAvailable = isGasPrimary;
          } else if (stream === 'gas') {
            isRatioAvailable = isOilPrimary;
          } else {
            isRatioAvailable = isOilPrimary || isGasPrimary;
          }

          return (
            <form.Field name={`${fieldKey}.dcaModel` as never}>
              {(field: any) => (
                <Layer>
                  <Dropdown
                    id={`tc-dca-${stream}-decline-model`}
                    titleText="DCA Model"
                    label="Decline Model"
                    items={DECLINE_MODEL_ITEMS}
                    itemToString={(item) => {
                      if (!item) return '';
                      if (item.id === 'ratio' && !isRatioAvailable) {
                        return `${item.label} (unavailable)`;
                      }
                      return item.label;
                    }}
                    selectedItem={
                      DECLINE_MODEL_ITEMS.find(
                        (i) => i.id === field.state.value
                      ) ?? DECLINE_MODEL_ITEMS[0]
                    }
                    onChange={({ selectedItem }) => {
                      if (!selectedItem) return;
                      // Prevent selecting ratio when not available
                      if (selectedItem.id === 'ratio' && !isRatioAvailable) {
                        return;
                      }
                      field.handleChange(selectedItem.id);
                      // When switching to ratio, force ramp-up to none
                      if (selectedItem.id === 'ratio') {
                        form.setFieldValue(
                          `${fieldKey}.rampUpModel` as never,
                          'none' as never
                        );
                      }
                    }}
                  />
                </Layer>
              )}
            </form.Field>
          );
        }}
      </form.Subscribe>

      {/* DCA params: qi, di, bFactor, dlim — hidden when ratio model */}
      <form.Subscribe
        selector={(s: any) => ({
          dcaModel: s.values[fieldKey].dcaModel as DeclineModel,
          qiFormat: s.values.qiFormat as string,
          diFormat: s.values.diFormat as string,
        })}
      >
        {({
          dcaModel,
          qiFormat,
          diFormat,
        }: {
          dcaModel: DeclineModel;
          qiFormat: string;
          diFormat: string;
        }) => {
          if (dcaModel === 'ratio') return null;

          const qiTimeUnit = qiFormat === 'months' ? 'month' : 'day';
          const qiUnit = `${productionUnit}/${qiTimeUnit}`;
          const diUnit = diFormat === 'effective' ? '%/year' : '1/month';

          return (
            <>
              <div className={styles.volumeModelRow}>
                <form.Field name={`${fieldKey}.qi` as never}>
                  {(field: any) => {
                    const error = getFirstFieldError(field);
                    return (
                      <Layer>
                        <LockableInput
                          inputType="number"
                          inputMode="numeric"
                          isLocked={isLocked('dca', 'qi')}
                          onToggleLock={() =>
                            toggleLock('dca', 'qi', field.state.value)
                          }
                          id={`tc-dca-${stream}-qi`}
                          labelText={`qi ${qiUnit}`}
                          max={100_000_000}
                          min={0}
                          size="md"
                          value={field.state.value ?? ''}
                          placeholder="0"
                          invalid={!!error}
                          invalidText={error || ''}
                          onChange={(val) => field.handleChange(Number(val))}
                        />
                      </Layer>
                    );
                  }}
                </form.Field>

                <form.Field name={`${fieldKey}.di` as never}>
                  {(field: any) => {
                    const error = getFirstFieldError(field);
                    return (
                      <Layer>
                        <LockableInput
                          inputType="number"
                          inputMode="decimal"
                          isLocked={isLocked('dca', 'di')}
                          onToggleLock={() =>
                            toggleLock('dca', 'di', field.state.value)
                          }
                          id={`tc-dca-${stream}-di`}
                          labelText={`Di ${diUnit}`}
                          max={100}
                          min={0}
                          size="md"
                          value={field.state.value ?? ''}
                          placeholder="0"
                          invalid={!!error}
                          invalidText={error || ''}
                          onChange={(val) => field.handleChange(Number(val))}
                        />
                      </Layer>
                    );
                  }}
                </form.Field>
              </div>

              {(dcaModel === 'hyperbolic' ||
                dcaModel === 'modified_hyperbolic') && (
                <div className={styles.volumeModelRow}>
                  <form.Field name={`${fieldKey}.bFactor` as never}>
                    {(field: any) => {
                      const error = getFirstFieldError(field);
                      return (
                        <Layer>
                          <LockableInput
                            inputType="number"
                            inputMode="decimal"
                            isLocked={isLocked('dca', 'bFactor')}
                            onToggleLock={() =>
                              toggleLock('dca', 'bFactor', field.state.value)
                            }
                            id={`tc-dca-${stream}-bfactor`}
                            labelText="b factor"
                            max={10}
                            min={0}
                            size="md"
                            value={field.state.value ?? ''}
                            placeholder="0"
                            invalid={!!error}
                            invalidText={error || ''}
                            onChange={(val) => field.handleChange(Number(val))}
                          />
                        </Layer>
                      );
                    }}
                  </form.Field>

                  {dcaModel === 'modified_hyperbolic' && (
                    <form.Field name={`${fieldKey}.dlim` as never}>
                      {(field: any) => {
                        const error = getFirstFieldError(field);
                        return (
                          <Layer>
                            <LockableInput
                              inputType="number"
                              inputMode="decimal"
                              isLocked={isLocked('dca', 'dlim')}
                              onToggleLock={() =>
                                toggleLock('dca', 'dlim', field.state.value)
                              }
                              id={`tc-dca-${stream}-dlim`}
                              labelText={`Dlim ${diUnit}`}
                              max={100}
                              min={0}
                              size="md"
                              value={field.state.value ?? ''}
                              placeholder="0"
                              invalid={!!error}
                              invalidText={error || ''}
                              onChange={(val) =>
                                field.handleChange(Number(val))
                              }
                            />
                          </Layer>
                        );
                      }}
                    </form.Field>
                  )}
                </div>
              )}
            </>
          );
        }}
      </form.Subscribe>

      {/* Ratio model fields: ri, tElf, m — visible only when dcaModel === 'ratio' */}
      <form.Subscribe
        selector={(s: any) => ({
          dcaModel: s.values[fieldKey].dcaModel as DeclineModel,
          oilParams: s.values.oilDcaParams as StreamDcaParams,
          gasParams: s.values.gasDcaParams as StreamDcaParams,
        })}
      >
        {({
          dcaModel,
          oilParams,
          gasParams,
        }: {
          dcaModel: DeclineModel;
          oilParams: StreamDcaParams;
          gasParams: StreamDcaParams;
        }) => {
          if (dcaModel !== 'ratio') return null;

          // Determine Ri units based on primary stream
          let riUnits = '';
          if (stream === 'oil') {
            riUnits = 'mcf/bbl';
          } else if (stream === 'gas') {
            riUnits = 'bbl/mcf';
          } else {
            // Water — primary is oil or gas
            if (isPrimaryModel(oilParams)) {
              riUnits = 'bbl/bbl';
            } else if (isPrimaryModel(gasParams)) {
              riUnits = 'bbl/mcf';
            }
          }

          return (
            <>
              <div className={styles.volumeModelRow}>
                <form.Field name={`${fieldKey}.ri` as never}>
                  {(field: any) => {
                    const error = getFirstFieldError(field);
                    return (
                      <Layer>
                        <LockableInput
                          inputType="number"
                          inputMode="decimal"
                          isLocked={isLocked('dca', 'ri')}
                          onToggleLock={() =>
                            toggleLock('dca', 'ri', field.state.value)
                          }
                          id={`tc-dca-${stream}-ri`}
                          labelText={`Ri ${riUnits}`}
                          max={10000}
                          min={0.01}
                          size="md"
                          value={field.state.value ?? ''}
                          placeholder="0.00"
                          invalid={!!error}
                          invalidText={error || ''}
                          onChange={(val) => field.handleChange(Number(val))}
                        />
                      </Layer>
                    );
                  }}
                </form.Field>

                <form.Field name={`${fieldKey}.tElf` as never}>
                  {(field: any) => {
                    const error = getFirstFieldError(field);
                    return (
                      <Layer>
                        <LockableInput
                          inputType="number"
                          inputMode="numeric"
                          isLocked={isLocked('dca', 'tElf')}
                          onToggleLock={() =>
                            toggleLock('dca', 'tElf', field.state.value)
                          }
                          id={`tc-dca-${stream}-telf`}
                          labelText="t_elf months"
                          max={10000}
                          min={1}
                          size="md"
                          value={field.state.value ?? ''}
                          placeholder="0"
                          invalid={!!error}
                          invalidText={error || ''}
                          onChange={(val) => field.handleChange(Number(val))}
                        />
                      </Layer>
                    );
                  }}
                </form.Field>
              </div>

              <div className={styles.volumeModelRow}>
                <form.Field name={`${fieldKey}.m` as never}>
                  {(field: any) => {
                    const error = getFirstFieldError(field);
                    return (
                      <Layer>
                        <LockableInput
                          inputType="number"
                          inputMode="decimal"
                          isLocked={isLocked('dca', 'm')}
                          onToggleLock={() =>
                            toggleLock('dca', 'm', field.state.value)
                          }
                          id={`tc-dca-${stream}-m`}
                          labelText="m"
                          max={1}
                          min={-1}
                          size="md"
                          value={field.state.value ?? ''}
                          placeholder="0.00"
                          invalid={!!error}
                          invalidText={error || ''}
                          onChange={(val) => field.handleChange(Number(val))}
                        />
                      </Layer>
                    );
                  }}
                </form.Field>
              </div>

              {/* Convert to Hyperbolic — disabled until backend supports TC context */}
              <Tooltip align="bottom" label="Not yet available for type curves">
                <Button size="sm" disabled>
                  Convert to Hyperbolic
                </Button>
              </Tooltip>
            </>
          );
        }}
      </form.Subscribe>
    </div>
  );
};
